import Enums from "@/enums/enums";
import { DecryptionIndexedDbCash, EncryptionIndexedDbCash } from "@/helper/shareHelper";

const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
const OptionExportDBName = "OptionExportDB";
const ObjectStore = {
  optionExport: "optionExport",
  optionExportProduct: "optionExportProduct",
};
const ObjectStoresDefaultCount = Object.keys(ObjectStore).length;
const OptionExportDBVersion = 2; // must increase when modify any store

const GetCurrentOptionExportDBVersion = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(OptionExportDBName);

    request.onsuccess = (e) => {
      const db = e.target.result;
      const objectStoresCount = db.objectStoreNames.length;
      db.close();
      resolve(objectStoresCount !== ObjectStoresDefaultCount ? 0 : db.version);
    };

    request.onerror = (e) => {
      resolve(0);
    };
  });
};

const DeleteOptionExportIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(OptionExportDBName);

    request.onsuccess = (e) => {
      resolve(true);
    };

    request.onerror = (e) => {
      console.error("Failed to delete database OptionExportDB:", e.target.error);
      resolve(false);
    };

    request.onblocked = (e) => {
      console.warn(
        "Deletion of database OptionExportDB is blocked, possibly because it's open in another tab or process."
      );
      resolve(false);
    };
  });
};

export const OpenOptionExportIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    if (!indexedDB) {
      reject(new Error("Your browser doesn't support IndexedBD"));
      return;
    }

    const currentOptionExportDBVersion = await GetCurrentOptionExportDBVersion();

    if (currentOptionExportDBVersion !== OptionExportDBVersion) {
      await DeleteOptionExportIndexedDB();
    }

    const request = indexedDB.open(OptionExportDBName, OptionExportDBVersion);

    request.onerror = (e) => {
      console.error(`Failed to open database OptionExportDB: ${e.target.error}`);
      resolve(null);
    };

    request.onsuccess = (e) => {
      const db = e.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (e) => {
      const db = e.target.result;

      if (!db.objectStoreNames.contains(ObjectStore.optionExport)) {
        db.createObjectStore(ObjectStore.optionExport, { keyPath: "optionExportKey", autoIncrement: false });
      }

      if (!db.objectStoreNames.contains(ObjectStore.optionExportProduct)) {
        db.createObjectStore(ObjectStore.optionExportProduct, { keyPath: null, autoIncrement: false });
      }
    };
  });
};

// #region OptionExportDocument
export const AddOptionExportToIndexedDB = (optionExportValue) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStore.optionExport], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.optionExport);
      const request = objectStore.get(optionExportValue.optionExportKey);

      request.onsuccess = () => {
        if (request.result) {
          const result = request.result;
          result.options = optionExportValue.options;
          const updateRequest = objectStore.put(result);
          updateRequest.onsuccess = () => {
            db.close();
            resolve(true);
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to get key: ${err}`);
            db.close();
            resolve(false);
          };
        } else {
          const addRequest = objectStore.add(optionExportValue);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve(true);
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve(false);
            } else {
              console.error(`Error to add new Option Export: ${err}`);
              db.close();
              resolve(false);
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to get key: ${err}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetOptionExportByKeyFromIndexedDB = (optionExportKey) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStore.optionExport])
        .objectStore(ObjectStore.optionExport)
        .get(optionExportKey);

      request.onsuccess = () => {
        const tab = request.result;
        db.close();
        resolve(tab);
      };

      request.onerror = (err) => {
        console.error(`Error to get tab: ${err}`);
        db.close();
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const ClearOptionExportFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStore.optionExport], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.optionExport)
        .clear();

      request.onerror = (event) => {
        console.error("Error deleting database.");
        db.close();
        resolve(false);
      };

      request.onblocked = (event) => {
        db.close();
        console.error("blocked: " + event);
        resolve(false);
      };

      request.onsuccess = (event) => {
        db.close();
        resolve(true);
      };
    } else {
      resolve(false);
    }
  });
};
// #endregion

// #region OptionExportProduct
export const AddOptionExportProductToIndexedDB = (key, optionExportProduct) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();

    if (db) {
      const objectStore = db
        .transaction([ObjectStore.optionExportProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.optionExportProduct);
      const encryptedOptionExportProduct = EncryptionIndexedDbCash(JSON.stringify(optionExportProduct));
      const request = objectStore.put(encryptedOptionExportProduct, key);

      request.onsuccess = (e) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e) => {
        console.error(`Error to updateRequest AddOptionExportProductToIndexedDB: ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const GetOptionExportProductFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();

    if (db) {
      const objectStore = db
        .transaction([ObjectStore.optionExportProduct], Enums.IDBTransactionMode.ReadOnly)
        .objectStore(ObjectStore.optionExportProduct);
      const request = objectStore.get(key);

      request.onsuccess = (e) => {
        db.close();

        if (request.result) {
          const optionExportProduct = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(optionExportProduct);
        } else {
          resolve(null);
        }
      };

      request.onerror = (e) => {
        console.error(`Error to request GetOptionExportProductFromIndexedDB: ${e.target.error}`);
        db.close();
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteOptionExportProductFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();

    if (db) {
      const objectStore = db
        .transaction([ObjectStore.optionExportProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.optionExportProduct);
      const request = objectStore.delete(key);

      request.onsuccess = (e) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e) => {
        console.error(`Error to request DeleteOptionExportProductFromIndexedDB: ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const ClearOptionExportProductFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenOptionExportIndexedDB();

    if (db) {
      const request = db
        .transaction([ObjectStore.optionExportProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStore.optionExportProduct)
        .clear();

      request.onsuccess = (e) => {
        db.close();
        resolve(true);
      };

      request.onerror = (e) => {
        console.error(`Error to request ClearOptionExportProductFromIndexedDB: ${e.target.error}`);
        db.close();
        resolve(false);
      };
    } else {
      resolve(false);
    }
  });
};

export const ClearAllOptionExportFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    await ClearOptionExportFromIndexedDB();
    await ClearOptionExportProductFromIndexedDB();
    resolve(true);
  });
};
